import { HTMLProps, PropsWithChildren, ReactNode, useEffect } from 'react';
import { twc } from 'react-twc';
import { MenuButton } from '~/modules/Elements/AKUI/Menu/MenuButton';
import Box from '~/modules/Elements/AKUI/Primitives/Box';
import Typography from '~/modules/Elements/AKUI/Primitives/Typography';

const MenuContainer = twc(Box)`
  w-full
  flex
  flex-col
  bg-transparent
  items-stretch
  justify-center
  [view-transition-name:menu-container]
  pointer-events-auto
`;

export const MenuHeader = twc(Typography)`text-xl text-active flex items-center justify-center`;
export const MenuHelpText = twc(Typography)`text-md`;

interface MenuProps extends PropsWithChildren, Omit<HTMLProps<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  spacing?: 'regular' | 'tight';
}

export const Menu = ({ title, children, className, spacing = 'regular', ref, ...props }: MenuProps) => {
  useEffect(() => {
    document.body.dataset.inputMode = document.body.dataset.inputMode || 'keyboard';

    const setKeyboard = (e: KeyboardEvent) => {
      const keys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', ' ', 'Tab'];
      if (keys.includes(e.key)) document.body.dataset.inputMode = 'keyboard';
    };

    const setMouse = () => {
      document.body.dataset.inputMode = 'mouse';
    };

    window.addEventListener('keydown', setKeyboard, { passive: true });
    window.addEventListener('pointermove', setMouse, { passive: true });
    window.addEventListener('pointerdown', setMouse, { passive: true });

    return () => {
      window.removeEventListener('keydown', setKeyboard);
      window.removeEventListener('pointermove', setMouse);
      window.removeEventListener('pointerdown', setMouse);
    };
  }, []);

  return (
    <MenuContainer {...props} className={`${className ?? ''} ${spacing === 'tight' ? 'gap-2.5' : 'gap-4'}`} ref={ref}>
      {title && <MenuHeader>{title}</MenuHeader>}
      {children}
    </MenuContainer>
  );
};

Menu.Header = MenuHeader;
Menu.Button = MenuButton;
Menu.ButtonGroup = twc.div`flex`;
Menu.HelpText = MenuHelpText;
Menu.Divider = () => <hr />;
